import React, { useMemo, useRef, useState } from 'react';
import { Button } from 'rebass';
import { Checkbox, Label, Input } from '@rebass/forms';

/**
 * ManageColumnsForm — presentation + interaction for the full column
 * manager modal.
 *
 * The form is intentionally "dumb" about persistence: callers pass in
 * pre-grouped `sections` (each with checked state already hydrated) and
 * a single `onSave(checkedIds)` callback. Everything to do with
 * localStorage, panel-property ids vs hidden ids, etc. lives in the
 * connected wrapper — the form only has to render checkboxes and
 * collect the new selection.
 *
 * Selection state lives in a ref-backed Set so flipping individual
 * checkboxes never causes a re-render of the list — important for the
 * "potentially hundreds of panel properties" case.
 *
 * @param {Object} props
 * @param {Array<{id:string,name:string,items:Array<{id:string,title:string,checked:boolean}>}>} props.sections
 * @param {boolean} props.isLoading
 * @param {() => void} props.onClose
 * @param {(checkedIds: string[]) => void} props.onSave
 */
export function ManageColumnsForm({ sections, isLoading, onClose, onSave }) {
    // Seed the selection set from the sections prop. Re-seeds once when
    // sections transition from empty to populated (queries resolve after
    // the modal opens with isLoading=true / sections=[]).
    const selectedRef = useRef(null);
    const seededFromRef = useRef(0);
    const itemCount = useMemo(() => sections.reduce((n, s) => n + s.items.length, 0), [sections]);

    if (selectedRef.current === null || (seededFromRef.current === 0 && itemCount > 0)) {
        selectedRef.current = new Set();
        sections.forEach(section => {
            section.items.forEach(item => {
                if (item.checked) selectedRef.current.add(item.id);
            });
        });
        seededFromRef.current = itemCount;
    }

    const [searchKeyword, setSearchKeyword] = useState('');
    const listRef = useRef(null);

    const noSearchResults = useMemo(() => {
        if (!searchKeyword.trim()) return false;
        const needle = searchKeyword.toLowerCase().trim();
        return sections.every(section => section.items.every(item => item.title.toLowerCase().indexOf(needle) === -1));
    }, [searchKeyword, sections]);

    /**
     * Bulk-toggle every presented checkbox.
     *
     * Reads the actual DOM inputs so the UI updates without a React
     * re-render and mirrors to the ref in lock-step.
     */
    const setAll = value => {
        const root = listRef.current;
        if (!root) return;
        // Only toggle checkboxes whose parent item row is visible (not
        // hidden by the search filter).
        const visibleItems = root.querySelectorAll('li[data-item-title]:not([hidden])');
        visibleItems.forEach(li => {
            const el = li.querySelector('input[type="checkbox"][data-column-id]');
            if (!el) return;
            el.checked = value;
            const id = el.dataset.columnId;
            if (!id) return;
            if (value) selectedRef.current.add(id);
            else selectedRef.current.delete(id);
        });
    };

    /**
     * Filter rows (and their parent sections) by substring title match.
     * Pure DOM work — no state churn.
     */
    const applySearch = keyword => {
        const root = listRef.current;
        if (!root) return;
        const needle = (keyword || '').toLowerCase().trim();
        const items = root.querySelectorAll('li[data-item-title]');
        items.forEach(li => {
            const title = li.dataset.itemTitle || '';
            const hit = needle.length === 0 || title.indexOf(needle) > -1;
            li.hidden = !hit;
        });
        // Hide whole sections that have no visible items left.
        const sectionLis = root.querySelectorAll('li[data-section-id]');
        sectionLis.forEach(sectionLi => {
            const hasVisible = sectionLi.querySelector('li[data-item-title]:not([hidden])');
            sectionLi.hidden = !hasVisible;
        });
    };

    const onCheckboxChange = columnId => event => {
        if (event.target.checked) selectedRef.current.add(columnId);
        else selectedRef.current.delete(columnId);
    };

    const onSubmit = event => {
        event.preventDefault();
        onSave(Array.from(selectedRef.current));
        onClose();
    };

    if (isLoading) {
        return <p className="color-text-secondary">Loading columns…</p>;
    }

    if (!itemCount) {
        return (
            <>
                <p className="color-text-secondary">
                    Nothing to manage yet — this study doesn't have any columns to show.
                </p>
                <div className="modal-actions">
                    <Button type="button" onClick={onClose} variant="secondary-gray" className="modal-secondary !m-0">
                        Close
                    </Button>
                </div>
            </>
        );
    }

    return (
        <form id="manage-columns-form" onSubmit={onSubmit}>
            <p className="color-text-secondary mb-3">
                Pick which columns appear in this study's overview. Study built-ins, screener questions, and panel
                properties all live here.
            </p>

            <div className="mb-3">
                <Input
                    type="search"
                    className="theme-input w-full"
                    placeholder="Search columns…"
                    defaultValue=""
                    onChange={event => {
                        const value = event.target.value;
                        setSearchKeyword(value);
                        applySearch(value);
                    }}
                />
            </div>

            <div className="flex gap-4 mb-3">
                <Button variant="link" type="button" className="muted" onClick={() => setAll(true)}>
                    Select All
                </Button>
                <Button variant="link" type="button" className="muted" onClick={() => setAll(false)}>
                    Deselect All
                </Button>
            </div>

            {noSearchResults && (
                <p className="text-center m-0 leading-8 color-text-secondary">No columns match your search.</p>
            )}

            <ul ref={listRef} className="list-none p-0 m-0">
                {sections.map(section => (
                    <li key={section.id} className="mb-4 !ml-0" data-section-id={section.id}>
                        <h3 className="fs-body-12 medium color-text-secondary uppercase !m-0 mb-1.5 tracking-wide">
                            {section.name}
                        </h3>
                        <ul className="list-none p-0 m-0">
                            {section.items.map(item => (
                                <li key={item.id} data-item-title={item.title.toLowerCase()} className="!m-0">
                                    <Label className="items-center text-primary hover-bg-gray !m-0 py-1.5 px-2 cursor-pointer normal rounded">
                                        <span className="shrink-0">
                                            <Checkbox
                                                data-column-id={item.id}
                                                defaultChecked={item.checked}
                                                onChange={onCheckboxChange(item.id)}
                                            />
                                        </span>
                                        <span className="ellipsis ml-1">{item.title}</span>
                                    </Label>
                                </li>
                            ))}
                        </ul>
                    </li>
                ))}
            </ul>

            <div className="modal-actions modal-actions-sticky">
                <Button type="button" onClick={onClose} variant="secondary-gray" className="modal-secondary !mr-2">
                    Cancel
                </Button>
                <Button type="submit" variant="primary" className="modal-primary !m-0" form="manage-columns-form">
                    Save
                </Button>
            </div>
        </form>
    );
}
